import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { Observable, forkJoin, map, of, switchMap, take } from 'rxjs';
import { Layout } from '../../../../shared/models/layout';
import { Subject } from '../../../../shared/models/subject';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { SubjectSettingService } from '../../../../shared/services/subject-setting.service';
import { SubjectSetting } from '../../../../shared/models/subject-setting';
import { SubjectType } from 'app/shared/models/subject-type';
import { Relationship } from 'app/shared/models/relationship';
import { messageService } from 'app/shared/services/messages/message.module';
import {
  ChecklistResultItem,
  SubjectService,
} from 'app/shared/services/subject.service';
import { Process } from 'app/shared/models/process';
import { ProcessDialogComponent } from '../../processes/process-dialog/process-dialog.component';
import { ProcessService } from 'app/shared/services/process.service';
import { EventService } from 'app/shared/services/event.service';
import { Status } from 'app/shared/models/status';
import { RetentionPromptComponent } from '../subject-edit/retention-prompt/retention-prompt.component';
import { ResultTable } from '@devexpress/analytics-core/analytics-data';

@Component({
  selector: 'app-subject-dialog',
  templateUrl: './subject-dialog.component.html',
  styleUrls: ['./subject-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SubjectDialogComponent implements OnInit {
  subject: Subject;
  changedSubject: Subject;
  subjectSetting: SubjectSetting;

  layout: Layout;
  isNew = true;
  subjectType: SubjectType;
  possibleRelationships: Relationship[];
  selectedRelationship: Relationship;
  showRelationships = false;
  showSubject = true;
  hasRelationships = false;
  canSave = false;
  canNext = false;
  validationErrors = '';
  checkforExisting = false;
  saveChanges = true;
  showDialog = false;

  // dialog size is bound to sizeX and sizeY
  sizeX = 630;
  sizeY = 522;

  constructor(
    public dialogRef: MatDialogRef<SubjectDialogComponent>,
    private subjectService: SubjectService,
    private subjectSettingsService: SubjectSettingService,
    private processService: ProcessService,
    private eventService: EventService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit() {
    this.subject = new Subject(this.data.subject);
    this.isNew = this.subject.id ? false : true;
    this.subjectType = this.data.subjectType;
    this.checkforExisting = !this.data.ignoreExisting;
    this.saveChanges = !this.data.suppressSave;

    this.possibleRelationships = this.data.possibleRelationships;
    if (this.possibleRelationships && this.possibleRelationships.length > 0) {
      this.hasRelationships = true;
    }
    // get the subject settings so we can check for autogenerate when building the form
    this.subjectSettingsService
      .getBySubjectTypeId(this.subject.subjectTypeId)
      .subscribe((setting) => {
        this.subjectSetting = setting;
      });
  }

  subjectChanged(data) {
    this.changedSubject = data.subject;
    this.canSave =
      data.valid &&
      (!this.hasRelationships || this.selectedRelationship !== undefined);
    this.canNext = data.valid;
  }

  layoutChanged(layout) {
    if (layout) {
      this.layout = layout.layout;
      this.sizeX = layout.sizeX;
      this.sizeY = layout.sizeY;
      this.showDialog = true;
    }
  }

  relationshipChanged(e: MatSelectChange) {
    this.selectedRelationship = e.value;
    this.canSave = true;
  }

  next() {
    this.showSubject = false;
    this.showRelationships = true;
  }

  back() {
    this.showSubject = true;
    this.showRelationships = false;
  }

  save() {
    if (this.changedSubject && this.canSave) {
      // set the canSave flag to false to prevent multiple saves
      this.canSave = false;

      const name = this.subject.isReferral ? 'Referral' : this.subjectType.name;
      this.saveAndClose(
        this.subjectSetting && this.subjectSetting.autoGenerateReference,
        `${name} saved`
      );
    }
  }

  saveAndClose(autoGenerateReference?, message?) {
    this.subjectService
      .hydrate(this.changedSubject)
      .pipe(take(1))
      .subscribe((hydratedSubject) => {
        let s = new Subject(hydratedSubject);
        s.summary = s.getSummary(this.layout, true);
        // if date created not set, set it
        s.dateCreated = s.dateCreated || new Date().toDateString();

        // check change to category
        if (
          this.subject.categoryId !== this.changedSubject.categoryId &&
          this.changedSubject.category &&
          this.changedSubject.category.autoRestrict &&
          this.subject.restricted
        ) {
          messageService.info(
            `Security restrictions for the selected category have not been applied as this ${this.subject.subjectType.name} already has security restrictions.`,
            'Restrictions already applied.'
          );
        }
        if (this.saveChanges) {
          this.performChecklistProcessTypeCheck(this.changedSubject).subscribe(
            (rc: ChecklistResultItem) => {
              if (rc.cancelCount > 0) {
                messageService.error(
                  `Subject not saved as not all required checklists where completed.`
                );
              } else {
                this.subjectService.onAfterSave
                  .pipe(take(1))
                  .subscribe((savedSubject) => {
                    if (savedSubject) {
                      if (autoGenerateReference && savedSubject.id !== s.id) {
                        s = new Subject(savedSubject);
                        s.summary = s.getSummary(this.layout, true);

                        this.subjectService.onAfterSave
                          .pipe(take(1))
                          .subscribe((ss: Subject) => {
                            if (ss) {
                              // Save CheckList process records after subject has been saved.
                              this.SaveChecklistProcessRecords(
                                rc.processList,
                                ss
                              );
                              this.closeDialog(ss);
                            }
                          });
                        this.subjectService.save(s, null, false);
                      } else {
                        this.SaveChecklistProcessRecords(
                          rc.processList,
                          savedSubject
                        );
                        this.closeDialog(savedSubject);
                      }
                    } else {
                      // MRD - if subject is null then likely to be an error so don't auto close the dialog
                      // this.closeDialog(savedSubject);
                    }
                  });

                if (
                  rc.caseClosedStatusRecord &&
                  rc.caseClosedStatusRecord.promptForRetention === true
                ) {
                  this.dialog
                    .open(RetentionPromptComponent, {
                      data: s,
                    })
                    .afterClosed()
                    .subscribe((data) => {
                      if (data && data.data && data.data.retentionDate) {
                        //make save possible if a retention date has been set

                        // Do Save
                        this.subjectService.save(
                          s,
                          message,
                          this.checkforExisting
                        );
                      } else {
                        messageService.error(
                          `Subject not saved as required retention date not set.`
                        );
                      }
                    });
                } else {
                  // Do Save
                  this.subjectService.save(s, message, this.checkforExisting);
                }
              }
            }
          );
        } else {
          this.closeDialog(s);
        }
      });
  }

  closeDialog(subject: Subject) {
    this.dialogRef.close({
      subject,
      subjectTypeId: this.subjectType.id,
      relationship: this.selectedRelationship,
    });
  }

  // Perform checklist processType checks
  // Big thankyou to MRD for helping getting this method written and working - PJT
  performChecklistProcessTypeCheck(
    changedSubject: Subject
  ): Observable<ChecklistResultItem> {
    // 1. Use SubjectService to perform checklist processType checks and return a list of process records that need to be completed.
    // 2. Open a checklist dialog for each process record and get user to complete them
    // 3. When all checklist dialogs are closed, return the list of process records that were completed
    return this.subjectService
      .performChecklistProcessTypeCheck(changedSubject)
      .pipe(
        take(1),
        switchMap(
          async ([checklistProcess, caseClosedStatusRecord]: [Process[], Status]) => {
            // We have a list of process records that need to be completed in checklistProcess.

            var result: ChecklistResultItem = new ChecklistResultItem();
            result.caseClosedStatusRecord = caseClosedStatusRecord;
            result.cancelCount=0;

            // if no checklistProcess records then just return ok to save
            if (checklistProcess.length == 0) return result;

            // Loop over required process types and save process records.
            for(var i =0; checklistProcess &&  i < checklistProcess.length; i++) {
              const dialogRef = this.dialog.open(ProcessDialogComponent, {
                  disableClose: true
                });
                dialogRef.componentInstance.data =  {
                    processRec: checklistProcess[i],
                    saveRecord: false,
                    };                
                var dlg = await dialogRef.afterClosed().toPromise();                  
                  if (dlg && dlg.dlgCancelled === true)
                    {
                      result.cancelCount=result.cancelCount+1;
                      break;
                    }
                  else  
                  {
                    var processRec = dlg.process;
                    processRec.processType= dlg.processType;
                    result.processList.push(processRec);                    
                  }
              }
              return result;
          }
        )
      );
  }

  private SaveChecklistProcessRecords(processList: Process[], s: Subject) {
    if (!processList || processList.length < 1) return;
    
    var sub = [];
    processList?.forEach((i) => {
      var p = new Process(i);
      p.subjectId = s.id;
      p.subject = undefined;
      p.processType = null;      
      p.dateTimeCompleted = new Date();

      // this.processService.onAfterSave.pipe(take(1)).subscribe((_) => {
      //   // Fire update notification when process records have been saved. This is used by checklist widget to refresh the list.
      //   console.log("Creating Checklist widget Signal")
      //   this.eventService.ChecklistRecordAdded(this.subject);
      // });
      sub.push(this.processService.saveAsObservable(p, i.processType.name + ' Checklist saved'));
    });

    forkJoin(sub).pipe(take(1)).subscribe((response) => {
      console.log("Creating Checklist widget Signal")
      this.eventService.ChecklistRecordAdded(this.subject);
    });
  }
}
